import { useState } from 'react';
import axios from 'axios';
import { Send, X, Loader2, CheckCircle2, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  { id: 'renovation',  label: 'Renovation' },
  { id: 'rebuild',     label: 'Rebuild' },
  { id: 'remodel',     label: 'Remodel' },
  { id: 'new-opening', label: 'New opening' },
];

const INPUT_CLASS = 'w-full px-3 py-2 rounded-lg border text-sm outline-none';
const INPUT_STYLE = { borderColor: '#A3D4D4', fontFamily: 'Manrope', backgroundColor: 'white' };
const LABEL_CLASS = 'block text-[10px] font-bold uppercase tracking-wider mb-1';
const LABEL_STYLE = { fontFamily: 'Manrope', color: '#4A5D5D' };

const EMPTY = { name: '', email: '', company: '', phone: '', role: '', project_type: '', message: '' };

/**
 * Demo request form used by the Atrium promo CTA. Submits to
 * POST /api/demo-requests which always stores the request in MongoDB AND
 * (when a Resend key is configured server-side) emails andres at
 * heavenlyaisolutions.com. Falls back gracefully if email isn't wired yet —
 * the request is still captured.
 */
export default function DemoRequestDialog({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => { setForm(EMPTY); setSuccess(null); };

  const close = () => { onClose(false); setTimeout(reset, 300); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Your name is required'); return; }
    if (!form.email.trim() || !form.email.includes('@')) { toast.error('A valid email is required'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/demo-requests`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        phone: form.phone.trim() || null,
        role: form.role.trim() || null,
        project_type: form.project_type || null,
        message: form.message.trim() || null,
      });
      setSuccess(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="demo-request-dialog"
        className="w-[95vw] max-w-lg p-0 overflow-hidden"
        style={{ backgroundColor: '#F4F1EB' }}
      >
        <DialogTitle className="sr-only">Request an Atrium demo</DialogTitle>
        <DialogDescription className="sr-only">
          Tell us about your property and we'll be in touch.
        </DialogDescription>

        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4" style={{ backgroundColor: '#0E1F2C', color: '#A3D4D4' }}>
          <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(163,212,212,0.15)' }}>
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold" style={{ color: 'white', fontFamily: "'Cormorant Garamond', serif" }}>Request an Atrium demo</p>
            <p className="text-[10px] tracking-[0.15em] uppercase" style={{ fontFamily: 'Manrope' }}>Built by Heavenly AI Solutions</p>
          </div>
          <button onClick={close} className="p-1.5 rounded-full hover:bg-white/10" data-testid="demo-close">
            <X className="w-4 h-4" style={{ color: 'white' }} />
          </button>
        </div>

        {success ? (
          <div className="p-8 text-center" data-testid="demo-success">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: '#22C55E' }} />
            <h3 className="text-2xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>
              Thanks — we'll be in touch.
            </h3>
            <p className="text-sm leading-relaxed mt-2" style={{ fontFamily: 'Manrope', color: '#4A5D5D' }}>
              Your request has reached <span style={{ fontWeight: 700, color: '#1A2E2E' }}>Andres Mocny</span> at Heavenly AI Solutions.
              Expect a personal reply within one business day.
            </p>
            {!success.delivered && (
              <p className="text-[11px] mt-4 italic" style={{ fontFamily: 'Manrope', color: '#7A8C8C' }}>
                Saved · email delivery will resume once mail service is configured.
              </p>
            )}
            <button
              onClick={close}
              className="mt-6 px-5 py-2.5 rounded-full text-xs font-bold text-white"
              style={{ backgroundColor: '#1F4FBE', fontFamily: 'Manrope' }}
              data-testid="demo-success-close"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="p-5 sm:p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={LABEL_CLASS} style={LABEL_STYLE}>Your name *</label>
                <input data-testid="demo-name" className={INPUT_CLASS} style={INPUT_STYLE} value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Andres Mocny" autoFocus />
              </div>
              <div>
                <label className={LABEL_CLASS} style={LABEL_STYLE}>Email *</label>
                <input data-testid="demo-email" type="email" className={INPUT_CLASS} style={INPUT_STYLE} value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@dreamsresorts.com" />
              </div>
              <div>
                <label className={LABEL_CLASS} style={LABEL_STYLE}>Company / property</label>
                <input data-testid="demo-company" className={INPUT_CLASS} style={INPUT_STYLE} value={form.company} onChange={(e) => update('company', e.target.value)} placeholder="Dreams Rose Hall Resort" />
              </div>
              <div>
                <label className={LABEL_CLASS} style={LABEL_STYLE}>Role</label>
                <input data-testid="demo-role" className={INPUT_CLASS} style={INPUT_STYLE} value={form.role} onChange={(e) => update('role', e.target.value)} placeholder="General Manager" />
              </div>
              <div className="sm:col-span-2">
                <label className={LABEL_CLASS} style={LABEL_STYLE}>Phone (optional)</label>
                <input data-testid="demo-phone" className={INPUT_CLASS} style={INPUT_STYLE} value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="+1 (876) ..." />
              </div>
            </div>

            <div>
              <label className={LABEL_CLASS} style={LABEL_STYLE}>Project type</label>
              <div className="flex flex-wrap gap-2">
                {PROJECT_TYPES.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`demo-project-${p.id}`}
                    onClick={() => update('project_type', form.project_type === p.id ? '' : p.id)}
                    className="px-3 py-1.5 rounded-full text-[11px] font-bold transition-all"
                    style={{
                      backgroundColor: form.project_type === p.id ? '#1F4FBE' : 'white',
                      color: form.project_type === p.id ? 'white' : '#1A2E2E',
                      border: '1.5px solid #1F4FBE',
                      fontFamily: 'Manrope',
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={LABEL_CLASS} style={LABEL_STYLE}>What would you like to see?</label>
              <textarea
                data-testid="demo-message"
                rows={3}
                className={`${INPUT_CLASS} resize-none`}
                style={INPUT_STYLE}
                value={form.message}
                onChange={(e) => update('message', e.target.value)}
                placeholder="Briefly describe your transformation project, timeline, what you'd like Atrium to help with…"
              />
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={close} className="flex-1 px-4 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5" style={{ backgroundColor: '#EAE5D9', color: '#4A5D5D', fontFamily: 'Manrope' }}>
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="demo-submit"
                className="flex-1 px-4 py-2.5 rounded-lg text-xs font-bold text-white flex items-center justify-center gap-1.5 disabled:opacity-60"
                style={{ backgroundColor: '#1F4FBE', fontFamily: 'Manrope' }}
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {submitting ? 'Sending…' : 'Send request'}
              </button>
            </div>
            <p className="text-[10px] text-center" style={{ fontFamily: 'Manrope', color: '#7A8C8C' }}>
              Your request goes directly to Andres Mocny at Heavenly AI Solutions.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
